import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useAuthContext } from '../../../hooks/useAuthContext';
import { createReply } from '../../../services/firebase/forum';
import UserMentionInput from '../../common/UserMentionInput';
import Button from '../../common/Button';
import FileAttachment from '../feedback/FileAttachment';
import { PaperAirplaneIcon } from '@heroicons/react/24/outline';
import { forumReplyAutoSave } from '../../../services/ForumReplyAutoSave';

const ReplyComposer = ({ 
  questionId, 
  parentReplyId = null,
  onSubmit, 
  onCancel,
  autoFocus = true,
  placeholder = "Share your insights, ask questions, or add to the discussion. Use @ to mention other users!"
}) => {
  const { user, userProfile } = useAuthContext();
  const [content, setContent] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showDraftRecovered, setShowDraftRecovered] = useState(false);
  
  const editorRef = useRef(null);
  const autoSaveTimeoutRef = useRef(null);

  // Get text content from HTML for validation
  const getTextContent = (html) => {
    if (typeof window !== 'undefined') {
      const div = document.createElement('div');
      div.innerHTML = html;
      return div.textContent || div.innerText || '';
    }
    return html.replace(/<[^>]*>/g, '');
  };

  // Auto-save functionality
  const autoSave = useCallback(async () => {
    if (!hasUnsavedChanges || !content.trim()) return;
    
    try {
      const result = await forumReplyAutoSave.saveDraft(questionId, content);
      if (result.success) {
        setHasUnsavedChanges(false);
      }
    } catch (error) {
      console.error('Auto-save error:', error);
    }
  }, [hasUnsavedChanges, content, questionId]);

  // Debounced content change handler
  const debouncedContentChange = useCallback(
    debounce((newContent) => {
      forumReplyAutoSave.scheduleAutoSave(questionId, newContent, (error) => {
        if (error) {
          console.error('Scheduled auto-save failed:', error);
        } else {
          setHasUnsavedChanges(false);
        }
      });
    }, 300),
    [questionId]
  );

  const handleContentChange = useCallback((newContent) => {
    setContent(newContent);
    setHasUnsavedChanges(true);
    
    if (error) {
      setError(null);
    }
    
    debouncedContentChange(newContent);
  }, [error, debouncedContentChange]);

  // Simple debounce implementation
  function debounce(func, wait) {
    let timeout;
    const executedFunction = function(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
    
    executedFunction.cancel = () => {
      clearTimeout(timeout);
    };
    
    return executedFunction;
  }

  const validateReply = () => {
    const textContent = getTextContent(content);
    
    // Require text content (attachments are hidden)
    if (!textContent.trim()) {
      return 'Please add some content to your reply';
    }
    
    if (textContent.length > 3000) {
      return 'Reply must be 3000 characters or less';
    }
    
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!user || !questionId || isSubmitting) return;

    const validationError = validateReply();
    if (validationError) {
      setError(validationError);
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const replyData = {
        questionId,
        parentReplyId,
        content: content.trim(),
        attachments: attachments,
        authorId: user.uid,
        authorName: user.displayName || user.email || 'Anonymous',
        authorPhotoURL: userProfile?.profilePicture || userProfile?.basicInfo?.photoURL || user.photoURL || ''
      };

      // Create optimistic reply for immediate UI update
      const optimisticReply = {
        id: `temp-${Date.now()}`, // Temporary ID
        questionId,
        content: content.trim(),
        attachments: attachments,
        authorId: user.uid,
        authorName: user.displayName || user.email || 'Anonymous',
        authorPhotoURL: userProfile?.profilePicture || userProfile?.basicInfo?.photoURL || user.photoURL || '',
        likeCount: 0,
        status: 'active',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        isOptimistic: true // Flag to identify optimistic updates
      };

      // Clear form and show optimistic reply immediately
      setContent('');
      setAttachments([]);
      setHasUnsavedChanges(false);
      forumReplyAutoSave.clearDraft(questionId);
      
      // Notify parent component with optimistic reply
      if (onSubmit) {
        onSubmit(optimisticReply);
      }

      // Submit to backend
      const result = await createReply(replyData);

      if (result.success) {
        // Update the optimistic reply with real data
        const realReply = { ...result.data, isOptimistic: false };
        if (onSubmit) {
          onSubmit(realReply, optimisticReply.id); // Pass temp ID for replacement
        }
      } else {
        // Handle failure - remove optimistic reply and show error
        if (onSubmit) {
          onSubmit(null, optimisticReply.id, result.error); // Signal to remove optimistic reply
        }
        setError(result.error || 'Failed to post reply');
        
        // Restore form content on failure
        setContent(replyData.content);
        setAttachments(replyData.attachments || []);
        setHasUnsavedChanges(true);
      }
    } catch (error) {
      console.error('Error submitting reply:', error);
      
      let errorMessage = 'Failed to post reply. Please try again.';
      if (!navigator.onLine) {
        errorMessage = 'No internet connection. Please check your network and try again.';
      } else if (error.name === 'NetworkError' || error.message.includes('fetch')) {
        errorMessage = 'Network error. Please check your connection and try again.';
      }
      
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = useCallback(() => {
    forumReplyAutoSave.clearDraft(questionId);
    
    setContent('');
    setAttachments([]);
    setError(null);
    setHasUnsavedChanges(false);
    
    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current);
    }
    
    if (onCancel) {
      onCancel();
    }
  }, [questionId, onCancel]);

  // Load draft on mount
  useEffect(() => {
    const draft = forumReplyAutoSave.loadDraft(questionId);
    if (draft && draft.content) {
      setContent(draft.content);
      setHasUnsavedChanges(true);
      setShowDraftRecovered(true);
      setTimeout(() => setShowDraftRecovered(false), 3000);
    }
  }, [questionId]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
      }
      
      if (debouncedContentChange.cancel) {
        debouncedContentChange.cancel();
      }
      
      forumReplyAutoSave.cleanup();
    };
  }, [debouncedContentChange]);

  if (!user) {
    return (
      <div className="text-center py-6">
        <p className="text-gray-600 dark:text-gray-400 mb-4">
          Please log in to reply to this post.
        </p>
        <Button as="a" href="/auth" size="sm">
          Log In
        </Button>
      </div>
    );
  }

  return (
    <div className="reply-composer">
      <form onSubmit={handleSubmit} className="space-y-3">
        {/* Draft recovered notification */}
        {showDraftRecovered && (
          <div className="text-xs text-green-600 dark:text-green-400">
            Draft recovered
          </div>
        )}

        {/* User Mention Input - main focus */}
        <div>
          <UserMentionInput
            ref={editorRef}
            value={content}
            onChange={handleContentChange}
            placeholder={placeholder}
            maxLength={3000}
            error={!!error}
            disabled={isSubmitting}
            className="min-h-[120px] border-gray-300 dark:border-gray-600 focus:border-blue-500 focus:ring-blue-500"
            autoFocus={autoFocus}
          />
        </div>

        {/* File Attachments - TEMPORARILY HIDDEN */}
        {/* <FileAttachment
          files={attachments}
          onChange={setAttachments}
          disabled={isSubmitting}
        /> */}
        
        {error && (
          <div className="text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 p-2 rounded">
            {error}
          </div>
        )}

        {/* Form Actions - clean and simple */}
        <div className="flex items-center justify-end space-x-2 pt-2">
          <Button
            type="button"
            variant="ghost"
            onClick={handleCancel}
            disabled={isSubmitting}
            size="sm"
          >
            Cancel
          </Button>
          
          <Button
            type="submit"
            disabled={isSubmitting || !getTextContent(content).trim()}
            size="sm"
            className="bg-black hover:bg-gray-800 text-white border-0 px-6"
          >
            {isSubmitting ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Posting
              </>
            ) : (
              'Reply'
            )}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default ReplyComposer;